import { useState } from "react";
import type { Verdict } from "../../shared/domain.ts";
import type { UnlimitedPlay } from "../game/storage.ts";

/**
 * The last ten Unlimited Rounds.
 *
 * Unlimited used to keep nothing at all: the finished Round was overwritten the
 * instant the next song was drawn, so a good run left no trace and there was no
 * way to look back at a song you had just failed to name. This is that trace.
 *
 * Two layouts, one component. On a wide screen it sits beside the game as a
 * panel; on a phone — which the styling treats as the real product — it collapses
 * to a summary the player opens, so it never pushes the play button off screen.
 */

const MARK: Record<Verdict | "skipped", string> = {
  exact: "■",
  "artist-match": "■",
  miss: "■",
  skipped: "□",
};

function Squares({ verdicts }: { verdicts: (Verdict | "skipped")[] }) {
  return (
    <span className="recent__squares" aria-hidden="true">
      {verdicts.map((v, i) => (
        <span key={i} className={`recent__sq recent__sq--${v}`}>
          {MARK[v]}
        </span>
      ))}
    </span>
  );
}

function Row({ play }: { play: UnlimitedPlay }) {
  return (
    <li className={`recent__row recent__row--${play.status}`}>
      {play.artworkUrl ? (
        <img className="recent__art" src={play.artworkUrl} alt="" width={36} height={36} loading="lazy" />
      ) : (
        <span className="recent__art recent__art--empty" aria-hidden="true">
          ♪
        </span>
      )}

      <span className="recent__text">
        <span className="recent__title">{play.title}</span>
        <span className="recent__artist">{play.artist}</span>
      </span>

      <span className="recent__result">
        <Squares verdicts={play.verdicts} />
        <span className="recent__tries">
          {play.status === "won" ? `${play.verdicts.length}` : "—"}
        </span>
      </span>
    </li>
  );
}

export function RecentPlays({ plays }: { plays: UnlimitedPlay[] }) {
  const [open, setOpen] = useState(false);

  const solved = plays.filter((p) => p.status === "won").length;

  if (plays.length === 0) {
    return (
      <aside className="recent recent--empty">
        <h2 className="recent__head">Recent</h2>
        <p className="recent__hint">Songs you finish show up here.</p>
      </aside>
    );
  }

  return (
    <aside className={`recent${open ? " recent--open" : ""}`}>
      {/* The button is the header on mobile and inert decoration on desktop,
          where the list is always visible — hence aria-expanded rather than a
          separate control per layout. */}
      <button
        className="recent__toggle"
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <h2 className="recent__head">Recent</h2>
        <span className="recent__tally">
          {solved}/{plays.length}
        </span>
        <span className="recent__chev" aria-hidden="true">
          ▾
        </span>
      </button>

      <ol className="recent__list">
        {plays.map((play) => (
          <Row key={`${play.trackId}-${play.verdicts.length}`} play={play} />
        ))}
      </ol>
    </aside>
  );
}
